import React, { useState, useEffect, Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useStateContext } from "../components/contexts/ContextProvider";
import LoadContentSpinner from "../components/Utils/LoadContentSpinner";
import { Settings, Ticket, Globe, ArrowRight, Download, Calendar, Plus, ChevronDown, ShieldCheck, Zap, CreditCard, BarChart3, ScanLine, Sparkles, Check } from "lucide-react";
import axios from "axios";
import { apiUrl } from "../config/site";

const Testimoniales = lazy(() => import("../components/publicWebAppContent/Testimoniales"));

export default function Index() {

    // useEffect(() => {
    //     // Mettre à jour le titre de la page
    //     document.title = "Accueil site officiel - Events";

    // }, []);
    
    const imagePath = process.env.REACT_APP_IMAGE_PATH;
    const { user, loading } = useStateContext();
    const [eventsCount, setEventsCount] = useState(0);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [openFeatureIndex, setOpenFeatureIndex] = useState(0);
    const [activeBilletterieAtout, setActiveBilletterieAtout] = useState(0);

    const billetterieAtouts = [
        {
            icon: Zap,
            title: "Mise en ligne express",
            text: "Publiez vos tarifs, quotas et visuels en quelques minutes. Votre billetterie est prête avant votre campagne.",
        },
        {
            icon: CreditCard,
            title: "Paiements fiables",
            text: "Parcours d’achat fluide et sécurisé pour vos acheteurs — moins d’abandons, plus de conversions.",
        },
        {
            icon: BarChart3,
            title: "Pilotage en temps réel",
            text: "Suivez les ventes, l’affluence et vos performances depuis le web ou l’app, sans tableur interminable.",
        },
        {
            icon: ScanLine,
            title: "Entrées sous contrôle",
            text: "QR codes vérifiables, scan rapide : files d’attente réduites et expérience pro à l’entrée.",
        },
        {
            icon: ShieldCheck,
            title: "Sérénité juridique & image",
            text: "Une plateforme structurée pour rassurer partenaires, sponsors et public — billetterie qui fait sérieux.",
        },
        {
            icon: Sparkles,
            title: "Visibilité & portée",
            text: "Donnez de l’ampleur à vos événements : présence mobile, partage simple, découverte facilitée.",
        },
    ];

    const heroFeatureItems = [
        {
            badgeClassName: "text-blue-800 bg-blue-100",
            iconClassName: "las la-ticket-alt",
            badgeText: "Expérience",
            title: "Découvrez les meilleurs événements",
            description:
                "Concerts, festivals, conférences, shows et plus encore : trouvez rapidement les événements qui correspondent à vos envies et réservez vos places au bon moment.",
        },
        {
            badgeClassName: "text-green-800 bg-green-100",
            iconClassName: "las la-check",
            badgeText: "Billetterie instantanée",
            title: "Réservation en quelques clics",
            description:
                "Plus besoin de vous déplacer pour acheter vos billets. Choisissez votre ticket, payez en ligne et recevez une confirmation immédiate avec QR code intégré.",
        },
        {
            badgeClassName: "text-red-800 bg-red-100",
            iconClassName: "las la-lock",
            badgeText: "Sécurité",
            title: "Paiement et accès sécurisés",
            description:
                "Chaque transaction est sécurisée et chaque billet est vérifiable. Vous profitez d'une expérience fiable de l'achat jusqu'au contrôle à l'entrée.",
        },
    ];

    useEffect(() => {
        const fetchEventsCount = async () => {
            try {
                const { data } = await axios.get(apiUrl('public/events'), {
                    params: { count_only: 1 },
                });
                setEventsCount(data.data?.total ?? 0);
            } catch (error) {
                setEventsCount(0);
            } finally {
                setLoadingEvents(false);
            }
        };
        fetchEventsCount();
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            setActiveBilletterieAtout((prev) => (prev + 1) % billetterieAtouts.length);
        }, 3800);
        return () => clearInterval(id);
    }, [billetterieAtouts.length]);

    if (loading) {
        return <LoadContentSpinner />;
    }

    return (
        <main className="nc-PageHome relative overflow-hidden">
            <Helmet>
                <title>Events Billetterie - La solution pour tous vos événements</title>
                <meta 
                    name="description" 
                    content="Events Billetterie est la solution de billetterie événementielle en Côte d'Ivoire. Organisez et réservez vos événements. Achetez vos billets en ligne avec notre application mobile, publiez et promouvez vos propres événements." 
                />
                <meta
                    name="keywords"
                    content="Events Billetterie, billetterie événementielle, Côte d'Ivoire, Abidjan, Billetterie, publication d'événements, gestion d'événements, réservation de billets, promotion d'événements, plateforme d'événements, événements, achat tickets, événements à venir"
                />
                <meta property="og:title" content="Events Billetterie - La solution pour tous vos événements" />
                <meta
                    property="og:description"
                    content="Plateforme de création et de réservation de vos tickets d'événementiels facilement via notre application mobile en Côte d'Ivoire."
                />
                <meta property="og:image" content="https://eventsbilletterie.com/assets/images/facadeEvents.png" />
                <meta property="og:url" content="https://eventsbilletterie.com" />
                <meta name="twitter:card" content="summary_large_image" />

                {/* Canonical tag */}
                <link rel="canonical" href="https://eventsbilletterie.com" />
            </Helmet>


            <div className="nc-BgGlassmorphism absolute inset-x-0 md:top-10 xl:top-40 min-h-0 pl-20 py-24 flex overflow-hidden z-0" data-nc-id="BgGlassmorphism">

                <span className="block bg-[#ef233c] w-72 h-72 rounded-full mix-blend-multiply filter blur-3xl opacity-10 lg:w-96 lg:h-96" />
                <span className="block bg-[#04868b] w-72 h-72 -ml-20 mt-40 rounded-full mix-blend-multiply filter blur-3xl opacity-10 lg:w-96 lg:h-96 nc-animation-delay-2000" />
            </div>
            <div className="container relative space-y-24 mb-10 lg:space-y-28 lg:mb-15">
                <div className="relative w-full pb-0 pt-16 mt-5 lg:py-20 xl:py-24 2xl:py-32">
                    <div className="nc-BackgroundSection absolute inset-y-0 w-screen xl:max-w-[1340px] 2xl:max-w-screen-2xl left-1/2 transform -translate-x-1/2 xl:rounded-[40px] z-0 bg-gray-100 bg-opacity-80 dark:bg-opacity-100"
                        style={{ backgroundColor: "rgb(243 244 246 / 26%)" }}
                    />
                    
                    {/* Compteur d'événements flottant - Mobile: en haut avant le titre */}
                    {!loadingEvents && eventsCount > 0 && (
                        <Link to='/events' className="block">
                            <div 
                                className="lg:hidden absolute top-1 left-1/2 z-10 shadow-xl"
                                style={{
                                    animation: 'float 3s ease-in-out infinite',
                                    animationDelay: '0.5s',
                                    willChange: 'transform'
                                }}
                            >
                                <div className="bg-primary-600 text-gray-50 px-4 py-1 rounded-xl shadow-lg border border-primary-1 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <div className="bg-gray-50 rounded-full p-1.5">
                                            <Calendar className="h-4 w-4 text-primary-1" />
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-gray-50">Événements disponibles</div>
                                            <div className="text-xl font-bold text-gray-50">{eventsCount}+</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    )}
                    
                    {/* Mockup téléphone desktop */}
                    <div style={{ justifyContent: "center" }}
                        className="hidden lg:flex absolute right-4 bottom-20 max-w-md xl:!max-w-lg rounded-3xl overflow-hidden">
                        <img
                            style={{ color: "transparent" }}
                            src={`${imagePath}images/facadeEvents.png`}
                            alt="Application mobile Events Billetterie"
                            decoding="async"
                            loading="eager"
                            fetchPriority="high"
                        />
                        {/* Compteur d'événements flottant - Desktop: en bas du mockup */}
                        {!loadingEvents && eventsCount > 0 && (
                            <Link to='/events' className="block">
                                <div 
                                    className="absolute bottom-3 left-1/2 z-10 shadow-xl"
                                    style={{
                                        animation: 'float 3s ease-in-out infinite',
                                        animationDelay: '0.5s',
                                        willChange: 'transform'
                                    }}
                                >
                                    <div className="bg-primary-600 text-gray-50 px-4 py-2 rounded-xl shadow-lg border border-primary-1">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-gray-100 rounded-full p-1.5">
                                                <Calendar className="h-4 w-4 text-primary-1" />
                                            </div>
                                            <div>
                                                <div className="text-xs font-medium text-gray-50">Événements disponibles</div>
                                                <div className="text-xl font-bold text-gray-50">{eventsCount}+</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        )}
                    </div>
                    
                    <div className="relative flex md:mx-4 flex-col max-w-xl xl:max-w-2xl">
                        <h2 className="text-4xl md:text-5xl xl:text-5xl font-bold text-gray-800">
                            Prêt à vivre des <span className="text-primary-1">expériences</span> <span className="text-primary-1">inoubliables</span> ?
                        </h2>
                        <span className="block mt-6 text-lg text-gray-600">
                            Votre solution de billetterie incontournable pour tous vos événements. Achetez vos tickets en ligne, publiez et promouvez vos propres événements.
                        </span>
                        
                        <div className="block lg:hidden mt-10 max-w-2xl rounded-3xl overflow-hidden">
                            <img
                                className=""
                                alt="Application mobile Events Billetterie"
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                                style={{ color: "transparent" }}
                                src={`${imagePath}images/facadeEvents.png`}
                            />
                        </div>

                        {/* Boutons en campagne - couleurs basées sur le fond */}
                        <div className="mt-8 flex flex-col sm:flex-row gap-4 flex-wrap">
                            <Link 
                                to="/go" 
                                className="inline-flex bg-primary-600 items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                            >
                                <Download className="h-5 w-5" />
                                <span>Télécharger l'appli</span>
                            </Link>
                            
                            {user ? (
                                <Link 
                                    to="/dashboard/events/registration" 
                                    className="inline-flex bg-white text-primary-1 items-center justify-center gap-2 px-6 py-3 font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 hover:bg-primary-600 hover:text-white transition-all duration-300"
                                >
                                    <Plus className="h-5 w-5" />
                                    <span>Publier gratuitement</span>
                                </Link>
                            ) : (
                                <Link 
                                    to="/create-account" 
                                    className="inline-flex bg-white text-primary-1 items-center justify-center gap-2 px-6 py-3 font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 hover:bg-primary-600 hover:text-white transition-all duration-300"
                                >
                                    <Plus className="h-5 w-5" />
                                    <span>Publier gratuitement</span>
                                </Link>
                            )}

                            <Link 
                                to="/events" 
                                className="inline-flex bg-gray-700 items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                            >
                                <Calendar className="h-5 w-5" />
                                <span>Découvrir les événements</span>
                            </Link>
                            
                            
                        </div>
                    </div>
                </div>

                <div className="relative !mt-24 overflow-hidden rounded-3xl border border-gray-200/80 bg-gradient-to-br from-white via-gray-50/90 to-[#5bcfc5]/[0.07] shadow-lg dark:border-neutral-800 dark:from-neutral-900 dark:via-neutral-900 dark:to-[#5bcfc5]/10">
                    <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-600/10 blur-3xl" />
                    <div className="pointer-events-none absolute -left-16 bottom-0 h-64 w-64 rounded-full bg-[#5bcfc5]/15 blur-3xl" />

                    <div className="relative border-t border-gray-200/70 bg-white/40 px-4 pb-10 pt-8 backdrop-blur-sm dark:border-neutral-800 dark:bg-neutral-900/40 sm:px-10 lg:px-14">
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-6xl mx-auto">
                            {billetterieAtouts.map((item, index) => {
                                const Icon = item.icon;
                                const isActive = activeBilletterieAtout === index;
                                return (
                                    <button
                                        key={item.title}
                                        type="button"
                                        onClick={() => setActiveBilletterieAtout(index)}
                                        className={`group text-left rounded-2xl border p-4 sm:p-5 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 ${
                                            isActive
                                                ? "border-[#5bcfc5] bg-[#5bcfc5]/10 shadow-md scale-[1.02] ring-2 ring-[#5bcfc5]/40 dark:bg-[#5bcfc5]/15"
                                                : "border-gray-200/80 bg-white/70 hover:border-[#5bcfc5]/50 hover:bg-white hover:shadow-md dark:border-neutral-700 dark:bg-neutral-800/50 dark:hover:border-[#5bcfc5]/40"
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <span
                                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                                                    isActive
                                                        ? "bg-[#5bcfc5] text-white"
                                                        : "bg-gray-100 text-[#04868b] group-hover:bg-[#5bcfc5]/20 dark:bg-neutral-700 dark:text-[#5bcfc5]"
                                                }`}
                                            >
                                                <Icon className="h-5 w-5" aria-hidden />
                                            </span>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 dark:text-gray-50 text-sm sm:text-base">
                                                    {item.title}
                                                </h3>
                                                <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">
                                                    {item.text}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* <QrCodeSection /> */}


                <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 !mt-24">
                    <div style={{ top: '-2', width: '67%', height: '734px', zIndex: '1', left: '11rem', position: 'absolute', backgroundImage: `url(${imagePath}images/particles.png)` }} />
                    <div className="flex-grow w-full hidden sm:flex items-center justify-center overflow-visible">
                        <div className="relative inline-flex items-center justify-center px-6 py-4 sm:px-10 sm:py-8">
                            <img
                                className="pointer-events-none select-none absolute z-[4] w-[min(54%,14rem)] sm:w-[min(56%,16rem)] max-h-[min(44vh,260px)] object-contain rounded-xl shadow-[0_12px_40px_-8px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.06] animate-hero-float-a -left-[4%] sm:-left-[6%] top-[2%] sm:top-[4%]"
                                alt="Aperçu du parcours de paiement sécurisé"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/hero-float-paiement.png`}
                            />
                            <img
                                className="pointer-events-none select-none absolute z-[4] w-[min(52%,13.5rem)] sm:w-[min(54%,15.5rem)] max-h-[min(42vh,235px)] object-contain rounded-xl shadow-[0_12px_40px_-8px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.06] animate-hero-float-b -right-[6%] sm:-right-[8%] bottom-[6%] sm:bottom-[8%]"
                                alt="Aperçu de la confirmation de paiement"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/hero-float-confirmation.png`}
                            />
                            <img
                                className="relative z-[3] max-w-[18rem] sm:!max-w-[22rem]"
                                alt=""
                                loading="lazy"
                                decoding="async"
                                style={{ color: "transparent" }}
                                srcSet={`${imagePath}images/banniere.png`}
                                data-nimg="1"
                            />
                        </div>
                    </div>
                    <div className="flex-shrink-0 lg:mt-0 lg:pl-8">
                        <ul className="space-y-4">
                            {heroFeatureItems.map((item, index) => {
                                const isOpen = openFeatureIndex === index;
                                return (
                                    <li
                                        key={item.title}
                                        className="rounded-2xl border border-gray-200/70 bg-gray-100 backdrop-blur-sm p-4 sm:p-5 transition-all duration-300"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setOpenFeatureIndex(isOpen ? -1 : index)}
                                            className="w-full text-left"
                                            aria-expanded={isOpen}
                                        >
                                            <div className="flex items-center justify-between gap-4">
                                                <div>
                                                    <span className={`nc-Badge inline-flex px-2.5 py-1 rounded-full font-medium text-xs relative ${item.badgeClassName}`}>
                                                        <i className={`text-sm mr-1 ${item.iconClassName}`}></i>
                                                        {item.badgeText}
                                                    </span>
                                                    <span className="block text-xl font-semibold mt-3">
                                                        {item.title}
                                                    </span>
                                                </div>
                                                <ChevronDown
                                                    className={`h-5 w-5 text-gray-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                                                />
                                            </div>
                                        </button>

                                        {isOpen && (
                                            <p className="block mt-4 text-gray-600 dark:text-gray-400 leading-relaxed">
                                                {item.description}
                                            </p>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="mt-6">
                            <Link
                                to="/go"
                                className="relative inline-flex bg-primary-600 items-center justify-center gap-2 text-sm sm:text-sm font-normal px-4 py-2.5 sm:px-5 text-white rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 z-10"
                            >
                                <Download className="h-5 w-5" />
                                <span>Télécharger l'application</span>
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="py-14 pb-0 bg-gradient-to-b from-gray-50/80 to-white dark:from-neutral-950 dark:to-neutral-900 relative overflow-hidden">
                    <div className="max-w-5xl container mx-auto relative z-10 px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-14">
                            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#5bcfc5]/10 text-[#5bcfc5] mb-4 tracking-wide">
                                Pour les organisateurs
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-50 mb-4">
                                Votre billetterie, du premier billet au <span className="text-[#5bcfc5]">jour J</span>
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 sm:text-lg leading-relaxed">
                                Créez vos événements, pilotez les ventes et encaissez en toute transparence — du petit collectif aux grandes productions, sans vous noyer dans la technique.
                            </p>
                        </div>

                        <div className="rounded-2xl border border-gray-200/80 dark:border-neutral-700 bg-white/90 dark:bg-neutral-900/80 backdrop-blur-sm shadow-sm overflow-hidden mb-10">
                            <div className="p-5 sm:p-8">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
                                    {[
                                        {
                                            step: "1",
                                            icon: <Ticket className="h-5 w-5 text-white" aria-hidden />,
                                            title: "Créez votre événement",
                                            description:
                                                "Infos pratiques, visuels, tarifs et catégories de billets : tout est centralisé pour présenter votre événement comme vous l’imaginez.",
                                        },
                                        {
                                            step: "2",
                                            icon: <Settings className="h-5 w-5 text-white" aria-hidden />,
                                            title: "Paramétrez et publiez",
                                            description:
                                                "Quotas, prix, fenêtres de vente : vous gardez la main. Un clic pour mettre la billetterie en ligne quand vous êtes prêt.",
                                        },
                                        {
                                            step: "3",
                                            icon: <Globe className="h-5 w-5 text-white" aria-hidden />,
                                            title: "Diffusez et suivez",
                                            description:
                                                "Partagez votre lien, vendez sur le web et via l’app mobile, et consultez vos indicateurs en temps réel, en toute sérénité.",
                                        },
                                    ].map((step, index) => (
                                        <div
                                            key={index}
                                            className="relative rounded-xl border border-gray-100 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-800/40 p-5 transition-shadow hover:shadow-md hover:border-[#5bcfc5]/25 dark:hover:border-[#5bcfc5]/35"
                                        >
                                            <div className="flex items-start gap-4">
                                                {/* <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-[#5bcfc5] text-white shadow-sm">
                                                    {step.icon}
                                                </div> */}
                                                <div className="min-w-0">
                                                    <div className="flex items-center mb-2">
                                                        {/* <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-[#5bcfc5]/15 text-[#5bcfc5] text-[10px] font-bold">
                                                            {step.step}
                                                        </span> */}
                                                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base leading-snug">{step.title}</h4>
                                                    </div>
                                                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{step.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="max-w-2xl mx-auto mt-10 text-center">
                                    <div className="rounded-2xl bg-[#5bcfc5]/[0.07] dark:bg-[#5bcfc5]/10 border border-[#5bcfc5]/15 dark:border-[#5bcfc5]/25 px-6 py-8 sm:px-8">
                                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">Lancez votre prochain événement</h3>
                                        <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mb-6">
                                            Ouvrez un compte organisateur et publiez votre billetterie — vous ne payez que lorsque vous vendez.
                                        </p>
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                                            {user ? (
                                                <Link
                                                    to="/dashboard/events/registration"
                                                    className="inline-flex items-center justify-center h-11 px-6 font-medium text-white bg-gray-800 dark:bg-gray-700 hover:bg-gray-700 rounded-xl transition-colors shadow-sm"
                                                >
                                                    Créer ma billetterie
                                                    <ArrowRight className="h-4 w-4 ml-2 shrink-0" aria-hidden />
                                                </Link>
                                            ) : (
                                                <Link
                                                    to="/login"
                                                    className="inline-flex items-center justify-center h-11 px-6 font-medium text-white bg-gray-800 dark:bg-gray-700 hover:bg-gray-700 rounded-xl transition-colors shadow-sm"
                                                >
                                                    Créer ma billetterie
                                                    <ArrowRight className="h-4 w-4 ml-2 shrink-0" aria-hidden />
                                                </Link>
                                            )}
                                            <Link
                                                to="/offre-billetterie"
                                                className="inline-flex items-center justify-center h-11 px-5 font-medium text-gray-600 dark:text-[#5bcfc5] bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 hover:border-[#cccccc]/50 hover:bg-[#cccccc]/5 dark:hover:bg-neutral-700 rounded-xl transition-colors text-sm"
                                            >
                                                En savoir plus
                                            </Link>
                                        </div>
                                        <p className="mt-5 text-xs text-gray-500 dark:text-gray-400">
                                            Aucun frais de création de billetterie
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 !mt-24">
                    <div
                        className="flex-grow w-full flex justify-center items-center"
                    >
                        <img
                            className="w-full max-w-xs sm:max-w-sm h-auto object-contain"
                            alt="Paiement mobile"
                            loading="lazy"
                            width={350}
                            height={400}
                            decoding="async"
                            style={{ color: "transparent" }}
                            src={`${imagePath}images/Mobile_pay.png`}
                        />
                    </div>
                    <div className="flex-shrink-0 lg:mt-0 lg:pl-8">
                        <h2 className="font-bold text-4xl mt-3 mb-4">
                            {" "}
                            Gardez le contrôle{" "}
                        </h2>
                        <span className="text-gray-700 dark:text-gray-300">
                        L&apos;essentiel pour acheter, être informé et garder vos billets sous la main — sans multiplier les outils. Installez l&apos;appli et retrouvez la même puissance qu&apos;en ligne, avec le confort du mobile.
                        </span>
                        <div className="w-14 border-b border-gray-200 dark:border-gray-700 mt-3" />
                        <nav className="space-y-4 mb-8 mt-3">
                            <li className="flex items-center">
                                <span className="mr-4 inline-flex flex-shrink-0 ">
                                    <i className="las la-check-circle text-2xl font-bold"></i>
                                </span>
                                <span className="text-gray-700 font-semibold dark:text-gray-300">
                                En cas d'imprevu, simplifiez les demandes de remboursement directement depuis l'application.
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <Check className="h-5 w-5 shrink-0 text-[#5bcfc5] mt-0.5" aria-hidden />
                                <span><strong className="font-medium text-gray-900 dark:text-gray-100">Achat complet, comme sur le site</strong> — codes promo, offres parfois réservées à l&apos;app et paiement sécurisé : le même niveau de fonctionnalités, dans un parcours fluide.</span>
                            </li>
                            <li className="flex gap-3">
                                <Check className="h-5 w-5 shrink-0 text-[#5bcfc5] mt-0.5" aria-hidden />
                                <span><strong className="font-medium text-gray-900 dark:text-gray-100">Informé et prêt à sortir</strong> — notifications sur les nouveaux événements ; prochains rendez-vous et billets accessibles en un coup d&apos;œil.</span>
                            </li>
                            <li className="flex gap-3">
                                <Check className="h-5 w-5 shrink-0 text-[#5bcfc5] mt-0.5" aria-hidden />
                                <span><strong className="font-medium text-gray-900 dark:text-gray-100">Web et appli, une seule continuité</strong> — vous payez sur le site ? Vos billets se retrouvent dans l&apos;appli, reliés à votre compte participant certifié : tout voyage avec vous, sans rien perdre.</span>
                            </li>
                        </nav>
                        <div className="mt-8 flex flex-wrap items-center gap-4 z-10">
                            <Link
                                to="/go"
                                className="nc-Button relative h-auto inline-flex items-center justify-center gap-2 rounded-lg transition-colors text-sm sm:text-sm font-normal px-4 py-2.5 sm:px-5 ttnc-ButtonPrimary disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-gray-50"
                            >
                                <Download className="h-4 w-4 shrink-0" aria-hidden />
                                Télécharger l'application
                            </Link>
                            <Link
                                to="/events"
                                className="text-sm font-normal text-[#04868b] hover:text-[#5bcfc5] underline-offset-4 hover:underline"
                            >
                                Voir les événements
                            </Link>
                        </div>
                    </div>
                </div>

                {/* <div className="relative py-16 shadow-sm">
                    <div className="absolute inset-y-0 w-screen xl:max-w-[1340px] 2xl:max-w-screen-2xl left-1/2 transform -translate-x-1/2 xl:rounded-[40px] z-0 glass-card dark:bg-black/20"/>
                    <div className="">
                        <div className="relative grid md:grid-cols-3 gap-12">
                            <div className="relative flex flex-col items-center max-w-xs mx-auto">
                                <div className="text-center mt-auto">
                                    <h3 className="text-xl font-semibold">
                                        Votre billetterie dans la poche
                                    </h3>
                                    <span className="block mt-5 text-gray-600 dark:text-gray-400">
                                        Telechargez l'application Events et creez votre compte en quelques instants pour demarrer rapidement.
                                    </span>
                                </div>
                            </div>
                            <div className="relative flex flex-col items-center max-w-xs mx-auto">
                                <div className="text-center mt-auto">
                                    <h3 className="text-xl font-semibold">
                                        Explorez, choisissez, reservez
                                    </h3>
                                    <span className="block mt-5 text-gray-600 dark:text-gray-400">
                                        Parcourez une grande variete d'evenements, choisissez vos places et achetez vos billets en toute simplicite.
                                    </span>
                                </div>
                            </div>
                            <div className="relative flex flex-col items-center max-w-xs mx-auto">
                                <div className="text-center mt-auto">
                                    <h3 className="text-xl font-semibold">
                                        Entree rapide, experience reussie
                                    </h3>
                                    <span className="block mt-5 text-gray-600 dark:text-gray-400">
                                        Presentez votre billet numerique a l'entree et profitez de l'evenement sans attente inutile, de l'achat au scan final.
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex relative mt-6 justify-center items-center">
                            <div className="flex space-x-3 z-10">

                                <Link to="https://play.google.com/store/apps/details?id=com.event.event_app" target="_blank" rel="noopener noreferrer">
                                    <img
                                        alt="Android logo"
                                        loading="lazy"
                                        decoding="async"
                                        width={160}
                                        height={60}
                                        style={{ color: "transparent", height: "80%" }}
                                        srcSet={`${imagePath}images/btn-android.webp`}
                                        // src={`${imagePath}images/btn-android.webp`}
                                    />
                                </Link>
                                <Link to="https://apps.apple.com/ci/app/events-billetterie/id6738791983" target="_blank" rel="noopener noreferrer">
                                    <img
                                        alt="APP Store logo"
                                        width={180}
                                        height={60}
                                        loading="lazy"
                                        decoding="async"
                                        style={{ color: "transparent", height: "80%" }}
                                        srcSet={`${imagePath}images/btn-ios.webp`}
                                        // src={`${imagePath}images/btn-ios.webp`}
                                    />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div> */}

                <Suspense fallback={null}>
                    <Testimoniales />
                </Suspense>
            </div>
        </main>
    );
}
